/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircleDollarSign } from 'lucide-react';
import { GearIcon } from '@radix-ui/react-icons';

import {
  DataState, DataStateError, SegmentedControl, TableRowsSkeleton, Text, TriageBand,
  UnstyledButton,
} from '@/shared/ui';
import { useAgencyPermissions } from '@/features/agency';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import { ListingTable, type ListingColumn } from '@/shared/ui/components/basic/listing-table';
import { formatStatsCurrency } from '@/shared/utils/format-stats';
import { PageHeader } from '@features/property-management/components/PageHeader';

import { CreateSaleDrawer } from '../components/create-sale';
import { SaleBoard } from '../components/SaleBoard';
import { SaleStateBadge } from '../components/SaleStateBadge';
import {
  SaleFilterPanel,
  applySaleFilters,
  buildSaleFilterLabels,
  countSaleFilters,
  removeSaleFilterByLabel,
  saleBrokerOptions,
  type SaleFilters,
} from '../components/SaleFilterPanel';
import {
  SALE_STATES,
  useSaleStateLabel,
  type SaleState,
} from '../constants/sale-states';
import { useSales } from '../hooks/useSales';
import { useSalePaperworkMilestoneTypes } from '../hooks/useSalePaperworkMilestoneTypes';
import {
  SALE_TRIAGE_IDS,
  SALE_TRIAGE_TONES,
  applySaleTriage,
  countSaleTriage,
  type SaleTriageId,
} from '../constants/sale-triage';
import type { Sale } from '../types/sale.types';
import { navIconFor } from '@layout/agency/agency-nav';

type SaleRow = Sale & { id: string };

/**
 * The two readings of one query.
 *
 * The table leads, as in Locações: a sale is read by its value, its
 * paperwork progress and its broker — the columns one sorts on. The board
 * answers the other question, "what is still in paperwork".
 */
type SaleView = 'board' | 'table';

/**
 * How many required paperwork milestones still have no date. Shown as a
 * compact "2/3" rather than a list, so the column stays scannable while
 * still telling the broker where the work is.
 */
function paperworkProgress(sale: Sale, requiredMilestoneIds: readonly string[]): string {
  const done = requiredMilestoneIds.filter((id) => Boolean(sale.paperwork[id])).length;
  return `${done}/${requiredMilestoneIds.length}`;
}

/**
 * Vendas: what closed, and what is still being papered.
 *
 * Only concluded and in-progress *records* live here — a sale still being
 * negotiated is a Negócio, not a Venda. Keeping the two apart is the whole
 * point of the separation; showing in-flight sales here would make this
 * screen compete with Negócios.
 *
 * @returns The sales list page.
 */
export function SalesPage() {
  const { agency_id: agencyId = '' } = useParams<{ agency_id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('features/sales');
  const { canManageSalePaperworkMilestones } = useAgencyPermissions();
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const stateLabel = useSaleStateLabel();

  // The `state` query param predates the panel and keeps a filtered list
  // shareable by URL, so it seeds the panel once and is written back when the
  // state section changes. The panel is the source of truth after that.
  const [filters, setFilters] = useState<SaleFilters>(() => {
    const fromUrl = searchParams.get('state');
    const isKnown = SALE_STATES.includes(fromUrl as SaleState);
    return isKnown ? { states: [fromUrl as SaleState] } : {};
  });
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [triage, setTriage] = useState<SaleTriageId | null>(null);

  const { isLoading, isError, sales, refresh } = useSales(agencyId, search);
  const { types: milestoneTypes } = useSalePaperworkMilestoneTypes(agencyId);
  const requiredMilestoneIds = useMemo(
    () => milestoneTypes.filter((m) => m.required).map((m) => m.id),
    [milestoneTypes],
  );

  const syncStateToUrl = useCallback((next: SaleFilters) => {
    setSearchParams((params) => {
      // Only a lone state survives the round trip; a multi-select has no
      // single-value representation, so the param is dropped rather than
      // encoding a half-truth the next visitor would load as the whole set.
      if (next.states?.length === 1) params.set('state', next.states[0]);
      else params.delete('state');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const applyFilters = useCallback((next: SaleFilters) => {
    setFilters(next);
    syncStateToUrl(next);
  }, [syncStateToUrl]);

  const view: SaleView = searchParams.get('view') === 'board' ? 'board' : 'table';

  const setView = useCallback((next: string) => {
    setSearchParams((params) => {
      if (next === 'board') params.set('view', next);
      else params.delete('view');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const filtered = useMemo(
    () => applySaleFilters(sales, filters, requiredMilestoneIds),
    [sales, filters, requiredMilestoneIds],
  );
  // Counted over every sale, never over `filtered`: the band answers "what
  // needs me today", and a count that shrank as the broker typed in the
  // search box would answer "…among what you are looking at" instead.
  const triageCounts = useMemo(
    () => countSaleTriage(sales, milestoneTypes),
    [sales, milestoneTypes],
  );

  // After the filters and the search, so the narrowings compose and the
  // board and the table always show the same set.
  const visible = useMemo(
    () => applySaleTriage(filtered, triage, milestoneTypes),
    [filtered, triage, milestoneTypes],
  );

  const brokerOptions = useMemo(() => saleBrokerOptions(sales), [sales]);
  const filterCount = countSaleFilters(filters);
  const activeFilterLabels = useMemo(
    () => buildSaleFilterLabels(filters, t, stateLabel),
    [filters, t, stateLabel],
  );

  const columns: ListingColumn<SaleRow>[] = [
    {
      key: 'buyer_name',
      label: t('columns.buyer'),
      sortable: true,
      sortAccessor: (s) => s.buyer_name || null,
      render: (s) => <Text component="span">{s.buyer_name || '—'}</Text>,
    },
    {
      key: 'property_title',
      label: t('columns.property'),
      sortable: true,
      sortAccessor: (s) => s.property_title || s.property_id || null,
      render: (s) => (
        <Text component="span">{s.property_title || s.property_id || '—'}</Text>
      ),
    },
    {
      key: 'value',
      label: t('columns.value'),
      sortable: true,
      sortType: 'number',
      sortAccessor: (s) => s.accepted_value ?? null,
      render: (s) => (
        <Text component="span" className="tabular-nums">
          {formatStatsCurrency(s.accepted_value)}
        </Text>
      ),
    },
    {
      key: 'state',
      label: t('columns.state'),
      sortable: true,
      sortType: 'number',
      sortAccessor: (s) => {
        const idx = SALE_STATES.indexOf(s.state as SaleState);
        return idx === -1 ? SALE_STATES.length : idx;
      },
      render: (s) => <SaleStateBadge state={s.state} />,
    },
    {
      key: 'paperwork',
      label: t('columns.paperwork'),
      sortable: false,
      render: (s) => (
        <Text component="span" className="tabular-nums">
          {s.state === 'in_paperwork' ? paperworkProgress(s, requiredMilestoneIds) : '—'}
        </Text>
      ),
    },
    {
      key: 'broker',
      label: t('columns.broker'),
      sortable: true,
      sortAccessor: (s) => s.responsible_broker_email || null,
      render: (s) => <Text component="span">{s.responsible_broker_email || '—'}</Text>,
    },
  ];

  const hasActiveQuery = Boolean(search.trim()) || filterCount > 0;

  return (
    <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-[var(--surface-secondary)] font-[family-name:var(--font-family)]">
      <div className="flex flex-1 min-h-0 w-full flex-col gap-[var(--space-4)] overflow-auto p-[var(--space-5)] px-[var(--space-6)]">
        <PageHeader
          title={t('pageTitle')}
          icon={navIconFor('sales', 20)}
          ctaLabel={t('create.cta')}
          onCtaClick={() => setCreateOpen(true)}
          searchPlaceholder={t('searchPlaceholder')}
          searchValue={search}
          onSearchChange={setSearch}
          onFiltersToggle={() => setFiltersOpen((open) => !open)}
          activeFilters={activeFilterLabels}
          onRemoveFilter={(label) => applyFilters(
            removeSaleFilterByLabel(filters, label, t, stateLabel),
          )}
          onClearAllFilters={() => applyFilters({})}
          filterCount={filterCount}
          introLabel={t('intro.label')}
          introBody={t('intro.body')}
          introTestIdPrefix="sales"
          trailingAction={canManageSalePaperworkMilestones ? (
            <UnstyledButton
              type="button"
              data-testid="sales-settings-shortcut"
              onClick={() => navigate(`/app/agencies/${agencyId}/settings?section=vendas`)}
              style={settingsShortcutStyle}
            >
              <GearIcon width={14} height={14} />
              {t('settingsShortcut')}
            </UnstyledButton>
          ) : undefined}
        />

        <DataState
          isLoading={isLoading}
          isError={isError}
          isEmpty={false}
          loadingFallback={<TableRowsSkeleton rows={6} />}
          errorState={<DataStateError message={t('loadError')} onRetry={refresh} />}
        >
          {/* Above the count because it decides what is being counted. */}
          <TriageBand
            testId="sale-triage"
            groups={SALE_TRIAGE_IDS.map((id) => ({
              id,
              count: triageCounts[id],
              label: t(`triage.${id}.label`),
              hint: t(`triage.${id}.hint`),
              tone: SALE_TRIAGE_TONES[id],
              icon: <CircleDollarSign width={17} height={17} />,
            }))}
            selected={triage}
            onSelect={(id) => setTriage(id as SaleTriageId | null)}
          />

          {/* The count qualifies whichever view is on screen, so it shares
              the toggle's row rather than sitting on a line of its own. */}
          <div className="flex flex-wrap items-center justify-between gap-(--space-3)">
            <Text component="span" size="xs" className="text-(--text-tertiary)">
              {t('list.resultsCount', { count: visible.length })}
            </Text>
            <SegmentedControl
              value={view}
              onChange={setView}
              items={[
                { value: 'board', label: t('views.board') },
                { value: 'table', label: t('views.table') },
              ]}
              size="sm"
              testId="sales-view-toggle"
            />
          </div>

          {visible.length === 0 ? (
            <EmptyState
              icon={navIconFor('sales', 28)}
              title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
              description={
                hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')
              }
            />
          ) : view === 'board' ? (
            <SaleBoard agencyId={agencyId} sales={visible} />
          ) : (
            <div className="overflow-auto rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-sm)]">
              <ListingTable
                data={visible.map((s) => ({ ...s, id: s.id }))}
                columns={columns}
                onRowClick={(s) => navigate(`/app/agencies/${agencyId}/sales/${s.id}`)}
                testId="sales-listing-table"
              />
            </div>
          )}
        </DataState>
      </div>

      <SaleFilterPanel
        open={isFiltersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={applyFilters}
        onClear={() => applyFilters({})}
        initialFilters={filters}
        brokers={brokerOptions}
      />

      <CreateSaleDrawer
        agencyId={agencyId}
        open={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
    </div>
  );
}

/**
 * Matches the filters toggle beside it — same height, border and muted
 * text, so the two read as one pair of secondary controls rather than the
 * settings shortcut competing with the filter.
 */
const settingsShortcutStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-medium)' as unknown as number,
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
  whiteSpace: 'nowrap',
};

export default SalesPage;
